import { Classes } from "@blueprintjs/core";
import classNames from "classnames";

import { RubricDetailQuery, useRubricsQuery } from "@/graphql";
import { ensureNonNullArray } from "@/utils/array";

import { RubricCard } from "./RubricCard";

type Rubric = RubricDetailQuery["rubric"];

interface RubricsListProps {
  currentPage: number;
  query: string;
  source: string | null;
  onDataLoaded: (data: { totalRecords: number }) => void;
  onPreview: (rubric: Rubric) => void;
  excludedIds: string[];
}

export function RubricsList({
  currentPage,
  query,
  source,
  onDataLoaded,
  onPreview,
  excludedIds,
}: RubricsListProps) {
  const pageSize = 5;

  const { data, loading } = useRubricsQuery({
    variables: {
      currentPage,
      pageSize,
      search: query,
      source: source || undefined,
      status: "active", // Only show active rubrics
    },
    onCompleted: (data) => {
      onDataLoaded({ totalRecords: data.rubrics?.totalCount ?? 0 });
    },
  });

  if (loading) {
    return (
      <div className="flex flex-col gap-4 overflow-y-auto pr-3">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className={classNames(Classes.SKELETON, "round-corners h-[140px]")}
          />
        ))}
      </div>
    );
  }

  if (!data?.rubrics) {
    return <div>No rubrics found</div>;
  }

  const filteredRubrics = ensureNonNullArray(data.rubrics.items).filter(
    (rubric) => rubric && !excludedIds.includes(rubric.id),
  );

  return (
    <div className="flex flex-col gap-4 overflow-y-auto">
      {filteredRubrics?.map((rubric) => (
        <RubricCard key={rubric?.id} rubric={rubric} onPreview={onPreview} />
      ))}
    </div>
  );
}
